<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import { parsePhoneNumber } from "libphonenumber-js";
import moment from "moment";

import { getApiErrorMessage } from "@/helpers/error";

import Button from "@/dsComponents/buttons/Button.vue";
import FormSection from "@/dsComponents/FormSection.vue";

import { AskedChangeAction, type AskedChangeDisplayItem } from "./types";
import ValidateItem from "./ValidateItem.vue";
import { submitWorkerAskedChangesDecisions } from "./workerAskedChangesService";

interface ValidateBlockItemProps {
    item: AskedChangeDisplayItem;
    workerId: string;
    separator: boolean;
}

const props = defineProps<ValidateBlockItemProps>();

const emit = defineEmits<{
    "validated-item": [];
}>();

const { t } = useI18n();
const toaster = useToast();

const actionDone = ref<"" | "validated" | "refused">("");

const formatItemValues = (item: AskedChangeDisplayItem): AskedChangeDisplayItem => {
    switch (item.field) {
        case "phone":
            return {
                field: item.field,
                oldValue: item.oldValue ? parsePhoneNumber(item.oldValue, "FR").formatNational() : null,
                newValue: item.newValue ? parsePhoneNumber(item.newValue, "FR").formatNational() : null,
            };
        case "birthDate":
            return {
                field: item.field,
                oldValue: item.oldValue ? moment(item.oldValue).format("DD/MM/YYYY") : null,
                newValue: item.newValue ? moment(item.newValue).format("DD/MM/YYYY") : null,
            };
        default:
            return item;
    }
};

const handleAskedChanges = async ({ field, action }: { field: string; action: "reject" | "validate" }) => {
    try {
        await submitWorkerAskedChangesDecisions(props.workerId, {
            decisions: [{ field, action: action === "validate" ? AskedChangeAction.VALIDATE : AskedChangeAction.REJECT }],
        });
        actionDone.value = action === "validate" ? "validated" : "refused";
        toaster?.open({
            message: t(`page.worker.askedChanges.message.${action}`) as string,
            type: "success",
            duration: 5000,
        });
        emit("validated-item");
    } catch (error) {
        toaster?.open({
            message: getApiErrorMessage(error, "page.worker.askedChanges.message.error"),
            type: "error",
            duration: 5000,
        });
    }
};
</script>
<template>
    <div class="validate-bloc">
        <FormSection :title="t(`form.${item.field}`)" size="sm" :separator="separator" />
        <ValidateItem :item="formatItemValues(item)" />
        <div class="button-container">
            <Button
                v-if="!actionDone"
                full-width
                size="sm"
                variant="stroke"
                type="neutral"
                :label="t('global.actions.refuse')"
                @click="handleAskedChanges({ action: 'reject', field: item.field })"
            />
            <Button
                v-if="!actionDone"
                full-width
                size="sm"
                variant="filled"
                type="primary"
                :label="t('global.actions.validate')"
                @click="handleAskedChanges({ action: 'validate', field: item.field })"
            />
            <Button
                v-if="actionDone"
                full-width
                size="sm"
                variant="filled"
                type="primary"
                disabled
                :label="t(`page.worker.askedChanges.${actionDone}`)"
                right-icon="check"
            />
        </div>
    </div>
</template>
<style lang="scss" scoped>
.validate-bloc {
    display: flex;
    flex-direction: column;
    gap: 24px;

    .button-container {
        width: 100%;
        justify-content: center;

        display: flex;
        gap: 18px;
    }
}
</style>
